import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  signal,
  input,
  output,
  computed,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ButtonIconPosition, ButtonShape, ButtonSize, ButtonType } from './button.model';
import { DebounceClickDirective } from '../../../directives/debounce-click.directive';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'ui-button',
  imports: [NzButtonModule, NzSpinModule, DebounceClickDirective, IconComponent],
  template: `
    <button
      nz-button
      debounceClick
      [class]="buttonClasses()"
      [nzType]="buttonType()"
      [nzShape]="buttonShape()"
      [nzLoading]="loadingSignal()"
      [disabled]="disabled()"
      [nzBlock]="block()"
      [nzDanger]="danger()"
      [nzSize]="buttonSize()"
      [attr.aria-label]="ariaLabel()"
      (debounceClick)="handleClick()"
    >
      <!-- Icon on the left (if set) -->
       @if(icon() && iconPosition() === 'left') {
        <ui-icon [icon]="icon()!" />
       }

      <!-- Button text (can be any custom content) -->
      <ng-content></ng-content>

      <!-- Icon on the right (if set) -->
      @if(icon() && iconPosition() === 'right') {
        <ui-icon [icon]="icon()!" class="text-[9px] pt-0.5 mb-[-2px]" />
      }
    </button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent implements OnInit {
  // Input properties to control the button's behavior and appearance
  buttonType = input<ButtonType>('default');
  buttonShape = input<ButtonShape>(null);
  loading = input<boolean>(false);
  disabled = input<boolean>(false);
  block = input<boolean>(false);
  danger = input<boolean>(false);
  buttonText = input<string>('Button');
  class = input<string>('');
  icon = input<string | null>(null);
  iconPosition = input<ButtonIconPosition>('left');
  buttonSize = input<ButtonSize>('default');
  ariaLabel = input<string>('button');

  buttonClick = output<void>();

  commonClasses = 'flex items-center gap-x-2'
  buttonClasses = computed(() => `${this.buttonTypeMap[this.buttonType()] ?? ''} ${this.commonClasses} ${this.class()}`);

  loadingSignal = signal<boolean>(false);

  // Mapping of button types to Tailwind CSS classes
  private buttonTypeMap: { [key: string]: string } = {
    default: 'bg-gray-200 text-gray-800 border-gray-200 hover:bg-gray-300',
    primary: 'bg-primary text-white border-primary hover:bg-blue-600',
    dashed: 'bg-white text-gray-800 border border-dashed border-gray-300 hover:bg-gray-100',
    link: 'bg-transparent text-primary border-transparent hover:underline',
    text: 'bg-transparent text-gray-800 border-transparent hover:bg-gray-100 px-1',
    ghost: 'bg-transparent text-gray-500 border-gray-500 hover:bg-gray-100',
  };

  constructor() {}

  ngOnInit(): void {
    this.initButtonState()
  }

  private initButtonState(): void {
    // Initialize the loading signal and set button styles and classes
    this.loadingSignal.set(this.loading());
  }

  handleClick(): void {
    // Emit the click event but debounce rapid clicks
    this.buttonClick.emit();
  }
}
